"use client";

/**
 * Client body for the FC Agent (private infrastructure) page. Copy lives in
 * the `intgFcAgent` namespace; structure (icons, hrefs, capability status)
 * stays here.
 *
 * The connection diagram follows the house rule (V5.3): the visual never
 * appears without the plain-language numbered steps next to it — the
 * WorkflowSteps section directly below is that pairing.
 */

import {
  ArrowUp,
  ArrowUpRight,
  Download,
  Ban,
  CloudCog,
  Container,
  FileSignature,
  GitBranch,
  History,
  KeyRound,
  ListChecks,
  Lock,
  PlugZap,
  Radio,
  ShieldCheck,
  Workflow,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  Section,
  SectionHeader,
  WorkflowSteps,
  FeatureGrid,
  CapabilityGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { Reveal } from "@/components/reveal";
import { useLocale, useT } from "@/i18n";

/** The public package repository. The one external link on this page, and the
 *  answer to the question a platform team asks first. */
const AGENT_PACKAGE_URL = "https://github.com/FixControl-AI/fixcontrol-agent";

/** Token-only connection diagram: cloud on top, agent in the middle, the
 *  customer's systems below. The single arrow points UP — the agent dials out. */
function ConnectionDiagram() {
  const t = useT();
  const k = (s: string) => `intgFcAgent.diagram.${s}`;
  return (
    <Reveal>
      <div className="mx-auto mt-10 max-w-xl">
        <div className="card flex items-center gap-3 border-brand-500/40 bg-bg-raised p-4">
          <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand-grad text-white shadow-glow">
            <CloudCog size={18} />
          </span>
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-fg-bright">{t(k("cloud"))}</div>
            <div className="text-[12px] text-fg-muted">{t(k("cloudSub"))}</div>
          </div>
        </div>

        <div className="my-2 flex items-center justify-center gap-2 text-fg-muted">
          <ArrowUp size={16} className="text-brand-300" />
          <span className="text-[11px] font-mono uppercase tracking-[0.14em]">
            {t(k("arrow"))}
          </span>
        </div>

        <div className="card flex items-center gap-3 bg-bg-raised p-4">
          <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg border border-line-subtle bg-bg-elev text-brand-300">
            <Radio size={18} />
          </span>
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-fg-bright">{t(k("agent"))}</div>
            <div className="text-[12px] text-fg-muted">{t(k("agentSub"))}</div>
          </div>
        </div>

        <div className="mx-auto my-2 h-4 w-px bg-line-subtle" />

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {[
            { label: t(k("k8s")), icon: <Container size={16} /> },
            { label: t(k("argo")), icon: <GitBranch size={16} /> },
            { label: t(k("ci")), icon: <Workflow size={16} /> },
          ].map((s) => (
            <div
              key={s.label}
              className="card flex items-center gap-2 bg-bg-raised p-3"
            >
              <span className="text-fg-muted">{s.icon}</span>
              <span className="text-[13px] font-medium text-fg-dim">{s.label}</span>
            </div>
          ))}
        </div>

        <p className="mt-4 text-center text-[13px] leading-relaxed text-fg-muted">
          {t(k("caption"))}
        </p>
      </div>
    </Reveal>
  );
}

export function FcAgentContent() {
  const t = useT();
  const { locale } = useLocale();
  const k = (s: string) => `intgFcAgent.${s}`;

  const steps = [
    { key: "step1", icon: <Workflow size={18} /> },
    { key: "step2", icon: <ShieldCheck size={18} /> },
    { key: "step3", icon: <FileSignature size={18} /> },
    { key: "step4", icon: <Radio size={18} /> },
    { key: "step5", icon: <History size={18} /> },
  ].map((s) => ({
    icon: s.icon,
    tag: t(k(`how.${s.key}.tag`)),
    title: t(k(`how.${s.key}.title`)),
    body: t(k(`how.${s.key}.body`)),
  }));

  // How a customer actually obtains the agent. Separate from `how` on purpose:
  // that section is the runtime story (pause → gate → signed operation), this
  // one is the supply chain, and platform teams read them with different
  // questions in mind.
  const installSteps = [
    { key: "step1", icon: <Download size={18} /> },
    { key: "step2", icon: <FileSignature size={18} /> },
    { key: "step3", icon: <ListChecks size={18} /> },
    { key: "step4", icon: <ShieldCheck size={18} /> },
  ].map((s) => ({
    icon: s.icon,
    tag: t(k(`install.${s.key}.tag`)),
    title: t(k(`install.${s.key}.title`)),
    body: t(k(`install.${s.key}.body`)),
  }));

  const features = [
    { key: "outbound", icon: <PlugZap size={18} /> },
    { key: "private", icon: <Lock size={18} /> },
    { key: "signed", icon: <FileSignature size={18} /> },
    { key: "allowlist", icon: <ListChecks size={18} /> },
    { key: "rbac", icon: <ShieldCheck size={18} /> },
    { key: "credentials", icon: <KeyRound size={18} /> },
  ].map((f) => ({
    icon: f.icon,
    title: t(k(`features.${f.key}.title`)),
    body: t(k(`features.${f.key}.body`)),
  }));

  const capabilities = [
    { key: "collect", status: "supported" as const },
    { key: "ciGates", status: "conditional" as const },
    { key: "promote", status: "conditional" as const },
    { key: "validate", status: "conditional" as const },
    { key: "inbound", status: "unavailable" as const },
    { key: "arbitrary", status: "unavailable" as const },
  ].map((c) => ({
    name: t(k(`capabilities.${c.key}.name`)),
    status: c.status,
    body: t(k(`capabilities.${c.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5", "q6"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/security", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <Section>
        <SectionHeader
          eyebrow={t(k("diagram.eyebrow"))}
          title={t(k("diagram.title"))}
        />
        <ConnectionDiagram />
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("how.eyebrow"))} title={t(k("how.title"))} />
        <WorkflowSteps steps={steps} />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("what.eyebrow"))} title={t(k("what.title"))} />
        <FeatureGrid features={features} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("install.eyebrow"))}
          title={t(k("install.title"))}
          description={t(k("install.description"))}
        />
        <WorkflowSteps steps={installSteps} />
        <div className="mt-6 text-center">
          <a
            href={AGENT_PACKAGE_URL}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-300 hover:text-brand-200"
          >
            {t(k("install.linkLabel"))} <ArrowUpRight size={14} />
          </a>
        </div>
      </Section>

      <Section>
        <SectionHeader
          eyebrow={t(k("capabilities.eyebrow"))}
          title={t(k("capabilities.title"))}
          description={t(k("capabilities.description"))}
        />
        <CapabilityGrid
          capabilities={capabilities}
          statusLabels={{
            supported: t("mk.cap.supported"),
            conditional: t("mk.cap.conditional"),
            unavailable: t("mk.cap.unavailable"),
          }}
        />
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("failures.eyebrow"))} title={t(k("failures.title"))} />
        <SecurityNote icon={<Ban size={18} />} title={t(k("failures.noteTitle"))}>
          {t(k("failures.noteBody"))}
        </SecurityNote>
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/product/change-validation",
              label: t(k("related.validation.label")),
              description: t(k("related.validation.description")),
            },
            {
              href: "/security",
              label: t(k("related.security.label")),
              description: t(k("related.security.description")),
            },
            {
              href: "/integrations/argo",
              label: t(k("related.argo.label")),
              description: t(k("related.argo.description")),
            },
            {
              href: `/docs/${locale}/admin/fc-agent`,
              label: t(k("related.docs.label")),
              description: t(k("related.docs.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
